import React, { useEffect, useState } from 'react';
import { Button, Result, Space, Spin } from 'antd';
import {
  getCurrentSession,
  buildLoginRedirectUrl,
  getLoginUrl,
  logoutSession,
} from '../api/auth';
import { AuthProvider } from '../context/AuthContext';
import type { Session } from '../api/auth';
import { hasKccAccess } from '../utils/permissions';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [denied, setDenied] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currentSession = await getCurrentSession();
        if (cancelled) return;
        if (!currentSession) {
          window.location.href = buildLoginRedirectUrl();
          return;
        }
        if (!hasKccAccess(currentSession.identity?.traits)) {
          setSession(currentSession);
          setDenied(true);
          return;
        }
        setSession(currentSession);
      } catch {
        // whoami 网络/CORS 失败时同样跳转登录，避免白屏
        if (!cancelled) {
          window.location.href = buildLoginRedirectUrl();
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goAuthConsole = () => {
    const loginUrl = getLoginUrl();
    const base = loginUrl.replace(/\/login\/?$/i, '');
    window.location.href = base || loginUrl;
  };

  const handleLogout = async () => {
    const loginUrl = getLoginUrl();
    setLoggingOut(true);
    try {
      await logoutSession(loginUrl);
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = loginUrl;
    } finally {
      setLoggingOut(false);
    }
  };

  if (checking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Checking session..." />
      </div>
    );
  }

  if (denied) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="This account does not have Client Center (KCC) access. Please contact support."
          extra={
            <Space>
              <Button type="primary" onClick={goAuthConsole}>
                Auth console
              </Button>
              <Button loading={loggingOut} onClick={handleLogout}>
                Sign out
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return <AuthProvider session={session}>{children}</AuthProvider>;
};

export default AuthGuard;
